// ✅ src/pages/VehicleV2/BatteryCardMaintenanceEntry.jsx  (UPDATED FULL FILE)
// ✅ Updated pattern like WorkLocationMaster:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - init based loading
// - add/edit mode from querystring
// - loading + pageLoading + loadingDDL + saving states
// - same UI structure style
// - back button goes to /vehicle/BatteryCardMaintenanceListing
//
// Backend mount:
//   app.use("/api/batteryCardMaintenanceEntry", batteryCardMaintenanceEntry);

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaSave, FaArrowLeft, FaTimes, FaUndo } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceEntry";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const toISODate = (val) => {
  const s = String(val || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    const yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
  }

  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const mo = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }

  return "";
};

const emptyForm = {
  storeId: "",
  vehicleId: "",
  vehicleCode: "",
  vehicleName: "",
  vehicleRegNo: "",
  batteryTypeText: "",
  batteryId: "",
  batterySerialId: "",
  purchaseDate: "",
  fittedDate: "",
};

const BatteryCardMaintenanceEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branch =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const maintenanceId = useMemo(() => {
    return toInt(
      searchParams.get("batteryCardMainId") ||
        searchParams.get("maintenanceId") ||
        searchParams.get("batterycardmainid") ||
        searchParams.get("id"),
      0
    );
  }, [searchParams]);

  const isEdit = maintenanceId > 0;

  const [stores, setStores] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [batteryTypes, setBatteryTypes] = useState([]);
  const [batteryCodes, setBatteryCodes] = useState([]);
  const [batterySerials, setBatterySerials] = useState([]);

  const [branchName, setBranchName] = useState("");
  const [form, setForm] = useState(emptyForm);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingDDL, setLoadingDDL] = useState(false);
  const [saving, setSaving] = useState(false);

  const inputClass = useMemo(
    () =>
      `w-full h-10 px-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white"
          : "bg-white border-gray-300 text-gray-900"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `text-sm font-medium ${
        isDark ? "text-gray-200" : "text-gray-700"
      } whitespace-nowrap`,
    [isDark]
  );

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const fillFormFromEdit = useCallback((editData, allVehicles = [], defaultStoreId = "") => {
    const vehicleId = toStr(
      editData?.vehicleId ??
        editData?.fld_vehicleid ??
        editData?.vehicleid
    );

    const batteryId = toStr(
      editData?.batteryId ??
        editData?.fld_batteryid ??
        editData?.batteryid ??
        editData?.batteryCodeId
    );

    const batterySerialId = toStr(
      editData?.batterySerialId ??
        editData?.serialId ??
        editData?.batteryserialid ??
        batteryId
    );

    const matchedVehicle = allVehicles.find(
      (x) => String(x.id) === String(vehicleId)
    );

    setForm({
      storeId: toStr(
        editData?.storeId ??
          editData?.fld_storeid ??
          editData?.storeid ??
          defaultStoreId
      ),
      vehicleId: vehicleId,
      vehicleCode: toStr(
        editData?.vehicleCode ?? matchedVehicle?.code ?? ""
      ),
      vehicleName: toStr(
        editData?.vehicleName ?? matchedVehicle?.name ?? ""
      ),
      vehicleRegNo: toStr(
        editData?.vehicleRegNo ??
          editData?.vehicleRegno ??
          matchedVehicle?.regNo ??
          ""
      ),
      batteryTypeText: toStr(
        editData?.batteryTypeText ??
          editData?.batteryTypeMain ??
          editData?.batterytype ??
          ""
      ),
      batteryId: batteryId,
      batterySerialId: batterySerialId,
      purchaseDate: toISODate(
        editData?.purchaseDate ?? editData?.purchasedDate ?? ""
      ),
      fittedDate: toISODate(editData?.fittedDate ?? ""),
    });
  }, []);

  const loadVehicleDependent = useCallback(
    async (bId, vId, mId = 0) => {
      try {
        if (!bId || !vId) {
          setBatteryTypes([]);
          setBatteryCodes([]);
          setBatterySerials([]);
          return;
        }

        setLoadingDDL(true);

        const { data } = await axios.get(`${API_BASE}/vehicle`, {
          params: {
            branchId: toInt(bId, 0),
            vehicleId: toInt(vId, 0),
            maintenanceId: toInt(mId, 0),
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load vehicle data");
        }

        setBatteryTypes(Array.isArray(data?.data?.batteryTypes) ? data.data.batteryTypes : []);
        setBatteryCodes(Array.isArray(data?.data?.batteryCodes) ? data.data.batteryCodes : []);
        setBatterySerials(Array.isArray(data?.data?.batterySerials) ? data.data.batterySerials : []);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load battery details"
        );
        setBatteryTypes([]);
        setBatteryCodes([]);
        setBatterySerials([]);
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  const loadPurchaseDate = useCallback(
    async (batteryId) => {
      try {
        if (!batteryId) {
          setField("purchaseDate", "");
          return;
        }

        setLoadingDDL(true);

        const { data } = await axios.get(`${API_BASE}/battery/purchase-date`, {
          params: { batteryId: toInt(batteryId, 0) },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load purchase date");
        }

        setField("purchaseDate", toISODate(data?.data?.purchaseDate));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load purchase date"
        );
        setField("purchaseDate", "");
      } finally {
        setLoadingDDL(false);
      }
    },
    [commonHeaders]
  );

  /* ------------------------- INIT LOAD ------------------------- */
  const loadInit = useCallback(async () => {
    try {
      if (!branchId) {
        toast.error("branchId not found");
        return;
      }

      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: toInt(branchId, 0),
          loginUserId: toInt(loginUserId, 0),
          maintenanceId: isEdit ? maintenanceId : 0,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load initial data");
      }

      const d = data?.data || {};
      const initStores = Array.isArray(d?.stores) ? d.stores : [];
      const initVehicles = Array.isArray(d?.vehicles) ? d.vehicles : [];
      const initBatteryTypes = Array.isArray(d?.batteryTypes) ? d.batteryTypes : [];
      const initBatteryCodes = Array.isArray(d?.batteryCodes) ? d.batteryCodes : [];
      const initBatterySerials = Array.isArray(d?.batterySerials) ? d.batterySerials : [];

      setBranchName(toStr(d?.branchName));
      setStores(initStores);
      setVehicles(initVehicles);
      setBatteryTypes(initBatteryTypes);
      setBatteryCodes(initBatteryCodes);
      setBatterySerials(initBatterySerials);

      const defaultStoreId = toStr(initStores?.[0]?.id);

      if (d?.editData) {
        fillFormFromEdit(d.editData, initVehicles, defaultStoreId);

        const editVehicleId = toStr(
          d.editData?.vehicleId ?? d.editData?.fld_vehicleid
        );
        const editBatteryId = toStr(
          d.editData?.batteryId ??
            d.editData?.fld_batteryid ??
            d.editData?.batteryCodeId
        );

        if (editVehicleId) {
          await loadVehicleDependent(branchId, editVehicleId, maintenanceId);
        }
        if (editBatteryId) {
          await loadPurchaseDate(editBatteryId);
        }
      } else {
        setForm({
          ...emptyForm,
          storeId: defaultStoreId,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load initial data"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    branchId,
    loginUserId,
    isEdit,
    maintenanceId,
    commonHeaders,
    fillFormFromEdit,
    loadVehicleDependent,
    loadPurchaseDate,
  ]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  /* ------------------------- VEHICLE CHANGE ------------------------- */
  const onVehicleById = async (vehicleId) => {
    const vId = String(vehicleId || "");
    const matchedVehicle = vehicles.find((x) => String(x.id) === vId);

    setForm((prev) => ({
      ...prev,
      vehicleId: vId,
      vehicleCode: matchedVehicle?.code || "",
      vehicleName: matchedVehicle?.name || "",
      vehicleRegNo: matchedVehicle?.regNo || "",
      batteryTypeText: "",
      batteryId: "",
      batterySerialId: "",
      purchaseDate: "",
    }));

    setBatteryTypes([]);
    setBatteryCodes([]);
    setBatterySerials([]);

    if (!vId) return;
    await loadVehicleDependent(branchId, vId, maintenanceId);
  };

  const onVehicleCodeChange = async (e) => {
    const code = e.target.value;
    const matchedVehicle = vehicles.find((x) => String(x.code) === String(code));
    await onVehicleById(matchedVehicle?.id || "");
  };

  const onVehicleNameChange = async (e) => {
    const name = e.target.value;
    const matchedVehicle = vehicles.find((x) => String(x.name) === String(name));
    await onVehicleById(matchedVehicle?.id || "");
  };

  const onVehicleRegNoChange = async (e) => {
    const regNo = e.target.value;
    const matchedVehicle = vehicles.find((x) => String(x.regNo) === String(regNo));
    await onVehicleById(matchedVehicle?.id || "");
  };

  const onBatteryCodeChange = async (e) => {
    const batteryId = e.target.value;

    setForm((prev) => ({
      ...prev,
      batteryId,
      batterySerialId: batteryId,
    }));

    if (!batteryId) {
      setField("purchaseDate", "");
      return;
    }

    await loadPurchaseDate(batteryId);
  };

  /* ------------------------- VALIDATION ------------------------- */
  const canSave = useMemo(() => {
    return (
      toStr(form.storeId) &&
      toStr(form.vehicleId) &&
      toStr(form.batteryId) &&
      toStr(form.batterySerialId) &&
      toStr(form.batteryTypeText) &&
      toStr(form.fittedDate)
    );
  }, [form]);

  const validate = () => {
    if (!toStr(form.storeId)) {
      toast.error("Please select Store");
      return false;
    }
    if (!toStr(form.vehicleId)) {
      toast.error("Please select Vehicle");
      return false;
    }
    if (!toStr(form.batterySerialId)) {
      toast.error("Please select Battery SerialNo");
      return false;
    }
    if (!toStr(form.batteryId)) {
      toast.error("Please select Battery Code");
      return false;
    }
    if (!toStr(form.batteryTypeText)) {
      toast.error("Please select Battery Type");
      return false;
    }
    if (!toStr(form.fittedDate)) {
      toast.error("Please select Fitted Date");
      return false;
    }
    if (!toInt(branchId)) {
      toast.error("branchId not found");
      return false;
    }
    if (!toInt(loginUserId)) {
      toast.error("loginUserId not found");
      return false;
    }
    return true;
  };

  /* ------------------------- ACTIONS ------------------------- */
  const onSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const payload = {
        maintenanceId: isEdit ? maintenanceId : 0,
        batteryCardMainId: isEdit ? maintenanceId : 0,
        branchId: toInt(branchId, 0),
        loginUserId: toInt(loginUserId, 0),
        storeId: toInt(form.storeId, 0),
        vehicleId: toInt(form.vehicleId, 0),
        batteryId: toInt(form.batteryId, 0),
        batterySerialId: toInt(form.batterySerialId, 0),
        batteryTypeText: toStr(form.batteryTypeText),
        fittedDate: toStr(form.fittedDate),
        branchType: branch,
      };

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(
        data?.message || (isEdit ? "Updated successfully" : "Saved successfully")
      );

      setTimeout(() => {
        navigate("/vehicle/BatteryCardMaintenanceListing");
      }, 700);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const onClear = () => {
    if (isEdit) {
      loadInit();
      return;
    }

    setForm({
      ...emptyForm,
      storeId: stores?.[0]?.id ? String(stores[0].id) : "",
    });
    setBatteryTypes([]);
    setBatteryCodes([]);
    setBatterySerials([]);
  };

  const onBack = () => navigate("/vehicle/BatteryCardMaintenanceListing");

  const Row = ({ label, required, children }) => (
    <div className="grid grid-cols-[170px_1fr] items-center gap-4">
      <div className={labelClass}>
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </div>
      <div>{children}</div>
    </div>
  );

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BATTERY CARD MAINTENANCE ENTRY
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {isEdit ? "Edit Battery Card Maintenance" : "Add Battery Card Maintenance"}
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isEdit
                    ? isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                    : isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {isEdit ? `Edit Mode (#${maintenanceId})` : "New Entry"}
              </span>
            </div>

            <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-600"}`}>
              INFRA SCREEN : B-15/1
            </div>
          </div>

          <div
            className={`rounded-xl p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            {pageLoading ? (
              <div className="py-10 text-center">Loading...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-4">
                {/* LEFT COLUMN */}
                <div className="space-y-4">
                  <Row label="Branch Name">
                    <input
                      value={branchName || ""}
                      className={inputClass}
                      disabled
                    />
                  </Row>

                  <Row label="Vehicle Code" required>
                    <select
                      value={form.vehicleCode || ""}
                      onChange={onVehicleCodeChange}
                      className={inputClass}
                      disabled={saving || loadingDDL}
                    >
                      <option value="">Select</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.code}>
                          {v.code}
                        </option>
                      ))}
                    </select>
                  </Row>

                  <Row label="Vehicle RegNo">
                    <select
                      value={form.vehicleRegNo || ""}
                      onChange={onVehicleRegNoChange}
                      className={inputClass}
                      disabled={saving || loadingDDL}
                    >
                      <option value="">Select</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.regNo}>
                          {v.regNo}
                        </option>
                      ))}
                    </select>
                  </Row>

                  <Row label="BatteryCode" required>
                    <select
                      value={form.batteryId}
                      onChange={onBatteryCodeChange}
                      className={inputClass}
                      disabled={saving || loadingDDL || !form.vehicleId}
                    >
                      <option value="">Select</option>
                      {batteryCodes.map((b, idx) => (
                        <option key={b.id ?? idx} value={b.id}>
                          {b.code}
                        </option>
                      ))}
                    </select>
                  </Row>

                  <Row label="PurchasedDate">
                    <input
                      type="date"
                      value={form.purchaseDate || ""}
                      className={inputClass}
                      disabled
                    />
                  </Row>
                </div>

                {/* RIGHT COLUMN */}
                <div className="space-y-4">
                  <Row label="Store" required>
                    <select
                      value={form.storeId}
                      onChange={(e) => setField("storeId", e.target.value)}
                      className={inputClass}
                      disabled={saving}
                    >
                      <option value="">Select</option>
                      {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name || s.code}
                        </option>
                      ))}
                    </select>
                  </Row>

                  <Row label="Vehicle Name" required>
                    <select
                      value={form.vehicleName || ""}
                      onChange={onVehicleNameChange}
                      className={inputClass}
                      disabled={saving || loadingDDL}
                    >
                      <option value="">Select</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.name}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </Row>

                  <Row label="BatterySerialNo" required>
                    <select
                      value={form.batterySerialId}
                      onChange={(e) => setField("batterySerialId", e.target.value)}
                      className={inputClass}
                      disabled={saving || loadingDDL || !form.vehicleId}
                    >
                      <option value="">Select</option>
                      {batterySerials.map((b, idx) => (
                        <option key={b.id ?? idx} value={b.id}>
                          {b.serialNo}
                        </option>
                      ))}
                    </select>
                  </Row>

                  <Row label="BatteryType" required>
                    <select
                      value={form.batteryTypeText}
                      onChange={(e) => setField("batteryTypeText", e.target.value)}
                      className={inputClass}
                      disabled={saving || loadingDDL || !form.vehicleId}
                    >
                      <option value="">Select</option>
                      {batteryTypes.map((t, idx) => (
                        <option key={t.id ?? idx} value={t.type}>
                          {t.type}
                        </option>
                      ))}
                    </select>
                  </Row>

                  <Row label="FittedDate" required>
                    <input
                      type="date"
                      value={form.fittedDate || ""}
                      onChange={(e) => setField("fittedDate", e.target.value)}
                      className={inputClass}
                      disabled={saving}
                    />
                  </Row>
                </div>
              </div>
            )}

            {!pageLoading ? (
              <div className="mt-8">
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Select vehicle first. Battery type, code, serial and purchase date load based on selected vehicle.
                </div>
              </div>
            ) : null}

            {/* Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClear}
                disabled={saving || pageLoading}
                className="flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
              >
                {isEdit ? <FaUndo className="w-4 h-4" /> : <FaTimes className="w-4 h-4" />}
                {isEdit ? "Reload" : "Clear"}
              </button>

              <div className="flex gap-4">
                <button
                  onClick={onBack}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                >
                  <FaArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <button
                  onClick={onSave}
                  disabled={saving || pageLoading || loadingDDL || !canSave}
                  className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    saving || pageLoading || loadingDDL || !canSave
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass}`
                  }`}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />
                      {isEdit ? "Update" : "Save"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : BATTERY CARD MAINTENANCE ENTRY
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryCardMaintenanceEntry;